/* eslint-disable @typescript-eslint/no-explicit-any */
// (The "anys" are coming from a third party library)

const lowerCase = (str: string): string => str[0].toLowerCase() + str.slice(1)

const mapObject = (obj: any): any =>
	Object.fromEntries(Object.entries(obj).map(([k, v]) => [lowerCase(k), v]))
const mapArray = (arr: any[]): any => arr.map((obj) => mapObject(obj))

const mapAlbum = (album: any): Album => {
	const mappedAlbum = mapObject(album)
	mappedAlbum.number = mappedAlbum.id
	delete mappedAlbum.id
	mappedAlbum.catalogue = mapObject(album.Catalogue)
	return mappedAlbum
}

const formatComposerName = (composer: any): string => {
	const { firstName, lastName } = composer

	const composerName = [firstName, lastName].filter((name) => !!name).join(' ')

	return composerName
}

export function mapTracksFromAlgolia(
	algoliaTracks: any[],
	preferredOrder: number[] = null
): Track[] {
	const tracks = mapArray(algoliaTracks).map((track: any) => ({
		...track,
		startBpm: track.tempoStart,
		endBpm: track.tempoFinish,
		tempo: track.tempoName,
		startKey: track.keyStart,
		endKey: track.keyFinish,
		album: mapAlbum(track.album),
		composers: [...mapArray(track.composers)].map((composer) => ({
			...composer,
			name: formatComposerName(composer),
		})),
		publishers: [...mapArray(track.publishers)],
		mixVariation: track.mixVariation,
	}))

	if (preferredOrder) {
		const orderedTracks = preferredOrder
			.map((id) => tracks.find((item) => item.id === id))
			.filter((track) => track)

		return orderedTracks
	}

	return tracks
}

export function attachAiSearchHighlights(
	tracks: Track[],
	aiSearchResults: AiSearchResult[] | null
): Track[] {
	if (!aiSearchResults?.length) {
		return tracks
	}

	const highlightsById = new Map(
		aiSearchResults
			.filter((result) => result.highlights?.length)
			.map((result) => [result.id, result.highlights] as const)
	)

	if (highlightsById.size === 0) {
		return tracks
	}

	return tracks.map((track) => {
		const highlights = highlightsById.get(track.id)
		return highlights ? { ...track, highlights } : track
	})
}

/** Map Algolia hits, optionally reordering by AI search and attaching highlights. */
export function mapTracksForAiSearch(
	algoliaTracks: any[],
	aiSearchResults: AiSearchResult[] | null
): Track[] {
	if (!aiSearchResults) {
		return mapTracksFromAlgolia(algoliaTracks)
	}

	const preferredOrder = aiSearchResults.map((result) => result.id)

	return attachAiSearchHighlights(
		mapTracksFromAlgolia(algoliaTracks, preferredOrder),
		aiSearchResults
	)
}

export function getAlbumRestrictedAttributes(locale: string): string[] {
	let countryCode = locale.substring(3)
	const attributes = [
		'Album.Genre',
		'Album.SubGenre',
		'MusicalStyles',
		'Moods',
		'Title',
		'Instrumentations',
		'ProductionGenres',
		'Toolkit',
		'TempoName',
		'Album.Name',
		'Album.Artists',
		'Composers.FirstName',
		'Composers.LastName',
		'OriginalComposer',
		'MixType',
		'Eras',
		'AlbumTrackAliases',
		'TrackFilenames',
	]

	if (countryCode != 'GB' && countryCode != 'US' && countryCode != 'AU') {
		countryCode = countryCode[0] + countryCode[1].toLowerCase()
		attributes.push(
			`MusicalStyles_${countryCode}`,
			`Moods_${countryCode}`,
			`Instrumentations_${countryCode}`,
			`ProductionGenres_${countryCode}`,
			`Toolkit_${countryCode}`,
			`TempoName_${countryCode}`,
			`Album.Genre_${countryCode}`,
			`Album.SubGenre_${countryCode}`
		)
	}
	return attributes
}
